<template>
  <div ref="root" class="grid items-stretch gap-10 lg:grid-cols-2 lg:gap-12">
    <!-- min-w-0: text inputs carry a ~240px intrinsic width that would otherwise stretch the grid
         past narrow phone screens; capping the columns lets the fields shrink instead. -->
    <div class="flex min-w-0 flex-col gap-8">
      <!-- Deliberately not a <header> element: the theme positions bare header tags fixed. -->
      <div class="intro flex flex-col gap-4">
        <Chip />
        <!-- Keeps the design's size and weight; only the colour follows the theme (#6331). -->
        <h2 class="text-[32px] leading-9 font-medium lg:text-[44px] lg:leading-12">{{ title }}</h2>
        <p v-if="intro" class="text-lg leading-[22px] font-light text-body">{{ intro }}</p>
      </div>

      <!-- The form stays on screen through the whole flow on desktop; the step decides how frozen
           it is. A phone shows one block at a time (#6331): the form makes room for the blue panel
           the moment the calculation starts, and returns with "Opnieuw berekenen". -->
      <div class="swap-block rounded-xl bg-surface p-5 sm:p-8" :class="{ 'max-lg:hidden': step !== 'input' }">
        <InputStep :step="step" @submit="calculate" @restart="restart" />
      </div>
    </div>

    <!-- Hidden on a phone until the panel has something to say: the calculation runs, a result is
         in, or a failed request left its message and retry button there (the step stays input). -->
    <ExpertPanel
      ref="panel"
      :step="step"
      class="swap-block"
      :class="{ 'max-lg:hidden': step === 'input' && !panelSpeaks }"
      @retry="calculate"
      @restart="restart"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, useTemplateRef, watchEffect } from 'vue'

import Chip from '@/components/Chip.vue'
import ExpertPanel from '@/components/expert-panel/ExpertPanel.vue'
import InputStep from '@/components/steps/InputStep.vue'
import { trackCalculationFailed, trackCalculationStarted, trackCompleted } from '@/services/analytics'
import { prefersReducedMotion, useAnimationStore } from '@/stores/animation'
import { useCalculationStore } from '@/stores/calculation'
import { useInputStore } from '@/stores/input'
import type { Step } from '@/types/flow'
import { isPhoneLayout } from '@/utils/viewport'

defineProps<{
  title: string
  intro: string
}>()

const step = ref<Step>('input')

const input = useInputStore()
const calculation = useCalculationStore()
const animation = useAnimationStore()

// The failure message and its retry button live in the panel, also while the step is still input.
const panelSpeaks = computed(() => calculation.status === 'failed' || calculation.status === 'throttled')

/**
 * The request first, the animation after: capi owns the validation (#6322), so a rejected input
 * never gets a calculation to watch. The animation runs on its own timing anyway, also when the
 * answer arrived long before it ends.
 */
async function calculate() {
  input.clearErrors()

  await calculation.calculate(input.toCalculationInput())

  if (calculation.status === 'invalid') {
    // Back to the visitor: the messages render next to the fields they belong to.
    input.applyServerErrors(calculation.fieldErrors)

    return
  }

  // The request did not make it, or the limiter stopped it: the panel keeps the form as it is and
  // offers a retry.
  if (calculation.status === 'failed' || calculation.status === 'throttled') {
    return
  }

  trackCalculationStarted(input.contractType)
  animation.play()
  step.value = 'calculating'
  scrollToStage('panel')
}

function restart() {
  animation.reset()
  step.value = 'input'
  scrollToStage('tool')
}

const root = useTemplateRef('root')
const panel = useTemplateRef('panel')

/**
 * A phone swaps the form and the blue panel in place, which can leave the viewport somewhere in
 * the middle of the block that just appeared. Scrolling to that block lands the visitor on it: the
 * panel when the calculation starts, otherwise the tool, whose form is back on stage. Aiming at the
 * tool for the calculation would leave the checklist that carries the wait half below the fold, the
 * title and intro above it taking the room (#6331). Desktop shows both blocks, nothing to correct.
 */
async function scrollToStage(stage: 'tool' | 'panel') {
  if (!isPhoneLayout()) {
    return
  }

  await nextTick()

  const element = stage === 'panel' ? panel.value?.root : root.value

  element?.scrollIntoView?.({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' })
}

// The result appears when the show is over and the answer is in, whichever comes last.
watchEffect(() => {
  const answered = calculation.status === 'calculated' || calculation.status === 'unavailable'

  if (step.value === 'calculating' && animation.finished && answered) {
    step.value = 'result'

    // The moment the visitor sees the outcome, per the event table on #6321: an answer with
    // amounts completed the tool, an answer without means the tariffs were missing.
    if (calculation.status === 'calculated' && calculation.result?.difference) {
      trackCompleted(input.contractType, calculation.result.difference)
    } else {
      trackCalculationFailed(input.contractType)
    }
  }
})
</script>
